import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';
import { SHELL_CLASSES } from '../constants/boatClasses';
import { getSeatsForBoatClass } from '../utils/boatSeats';
import AthleteSearchInput from './AthleteSearchInput';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const emptySeats = (boatClass) =>
  getSeatsForBoatClass(boatClass).seats.map((seat) => ({ seat, athlete: '' }));

const seatLabelClass = (seat) => {
  if (seat === 'Cox') return 'bg-purple-100 text-purple-700 border-purple-300';
  if (seat === 'Stroke') return 'bg-blue-100 text-blue-700 border-blue-300';
  if (seat === 'Bow') return 'bg-green-100 text-green-700 border-green-300';
  return 'bg-gray-100 text-gray-700 border-gray-300';
};

const filledNames = (seats) => (seats || []).map((s) => s.athlete).filter(Boolean);

export default function LineupManager() {
  const { token, user, isAdmin } = useAuth();
  const [teams, setTeams] = useState([]);
  const [team, setTeam] = useState(user?.team || '');
  const [athletes, setAthletes] = useState([]);
  const [lineups, setLineups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: '', boatClass: '8+', seats: emptySeats('8+') });

  const flash = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 4000);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/athletes`, { headers: getAuthHeaders(token) });
        if (!res.ok) return;
        const data = await res.json();
        const uniqueTeams = [...new Set(data.filter((a) => a.team).map((a) => a.team))].sort();
        setTeams(uniqueTeams);
        setTeam((prev) => prev || (user?.team && uniqueTeams.includes(user.team) ? user.team : uniqueTeams[0]) || '');
      } catch (err) {
        console.error('Error fetching teams:', err);
      }
    })();
  }, [token, user?.team]);

  const loadLineups = useCallback(async (targetTeam) => {
    if (!targetTeam) {
      setLineups([]);
      return;
    }
    setLoading(true);
    try {
      const [lineupRes, athleteRes] = await Promise.all([
        fetch(`${API_URL}/api/lineups?team=${encodeURIComponent(targetTeam)}`, {
          headers: getAuthHeaders(token)
        }),
        fetch(`${API_URL}/api/athletes?team=${encodeURIComponent(targetTeam)}`, {
          headers: getAuthHeaders(token)
        })
      ]);
      if (lineupRes.ok) {
        const data = await lineupRes.json();
        setLineups(data.lineups || []);
      } else {
        const data = await lineupRes.json().catch(() => ({}));
        flash(data.error || 'Failed to load lineups');
        setLineups([]);
      }
      if (athleteRes.ok) {
        const roster = await athleteRes.json();
        setAthletes(Array.isArray(roster) ? roster : []);
      }
    } catch (err) {
      console.error('Error loading lineups:', err);
      flash('Error loading lineups');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadLineups(team);
  }, [team, loadLineups]);

  const athleteNames = athletes.filter((a) => !a.is_injured).map((a) => a.name);
  const coxNames = athletes.filter((a) => !a.is_injured && a.user_role === 'coxswain').map((a) => a.name);

  const availableForSeat = (seats, seatIndex) => {
    const taken = seats.map((s) => s.athlete).filter(Boolean);
    const current = seats[seatIndex]?.athlete;
    const pool = seats[seatIndex]?.seat === 'Cox' ? coxNames : athleteNames;
    return pool.filter((name) => name === current || !taken.includes(name));
  };

  const startCreate = () => {
    setEditingId('new');
    setDraft({ name: '', boatClass: '8+', seats: emptySeats('8+') });
  };

  const startEdit = (lineup) => {
    setEditingId(lineup.id);
    setDraft({
      name: lineup.name,
      boatClass: lineup.boatClass,
      seats: (lineup.seats || emptySeats(lineup.boatClass)).map((s) => ({
        seat: s.seat,
        athlete: s.athlete || ''
      }))
    });
  };

  const changeBoatClass = (boatClass) => {
    const nextSeats = emptySeats(boatClass);
    const prev = Object.fromEntries((draft.seats || []).map((s) => [s.seat, s.athlete]));
    setDraft({
      ...draft,
      boatClass,
      seats: nextSeats.map((s) => ({ ...s, athlete: prev[s.seat] || '' }))
    });
  };

  const updateSeat = (idx, athlete) => {
    const seats = [...draft.seats];
    seats[idx] = { ...seats[idx], athlete };
    setDraft({ ...draft, seats });
  };

  const saveDraft = async () => {
    const name = draft.name.trim();
    if (!name) {
      flash('Give this lineup a name (e.g. 1v)');
      return;
    }
    const payload = {
      team,
      name,
      boatClass: draft.boatClass,
      seats: draft.seats
    };
    const isNew = editingId === 'new';
    const url = isNew ? `${API_URL}/api/lineups` : `${API_URL}/api/lineups/${editingId}`;
    try {
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        flash(data.error || 'Failed to save lineup');
        return;
      }
      flash(isNew ? `Saved ${name}` : `Updated ${name}`);
      setEditingId(null);
      await loadLineups(team);
    } catch (err) {
      console.error('Error saving lineup:', err);
      flash('Error saving lineup');
    }
  };

  const deleteLineup = async (lineup) => {
    if (!confirm(`Delete ${lineup.name} (${lineup.boatClass})? This does not change workouts already posted.`)) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/lineups/${lineup.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        flash(data.error || 'Failed to delete lineup');
        return;
      }
      if (editingId === lineup.id) setEditingId(null);
      flash(`Deleted ${lineup.name}`);
      await loadLineups(team);
    } catch (err) {
      console.error('Error deleting lineup:', err);
      flash('Error deleting lineup');
    }
  };

  const renderSeatEditor = (seats, onSeatChange) => (
    <div className="space-y-2">
      {seats.some((s) => s.seat === 'Cox') && (
        <p className="text-xs text-gray-500">Cox can only be filled by someone with the Coxswain role.</p>
      )}
      {seats.map((seatData, idx) => (
        <div key={seatData.seat} className="flex items-center gap-2">
          <div className={`w-16 sm:w-20 flex-shrink-0 text-center py-2 px-2 rounded text-xs sm:text-sm font-semibold border ${seatLabelClass(seatData.seat)}`}>
            {seatData.seat}
          </div>
          <AthleteSearchInput
            value={seatData.athlete}
            onChange={(name) => onSeatChange(idx, name)}
            availableAthletes={availableForSeat(seats, idx)}
            placeholder={seatData.seat === 'Cox' ? 'Select coxswain...' : 'Select athlete...'}
          />
          {seatData.athlete && (
            <button
              type="button"
              onClick={() => onSeatChange(idx, '')}
              className="text-gray-400 hover:text-red-500 text-lg p-1"
              title="Clear seat"
            >
              ×
            </button>
          )}
        </div>
      ))}
    </div>
  );

  const byClass = SHELL_CLASSES
    .map((cls) => ({
      ...cls,
      lineups: lineups.filter((l) => l.boatClass === cls.value)
    }))
    .filter((group) => group.lineups.length > 0);

  return (
    <div className="p-2 sm:p-4">
      <h2 className="text-2xl font-bold mb-1">Lineups</h2>
      <p className="text-gray-600 text-sm mb-4">
        Save named crews by boat class (1v, 2v, 3v) and drop them into a shell in Workout Builder.
        Seat count and cox come from the class.
      </p>

      {message && (
        <div className={`mb-4 p-3 rounded text-sm ${/fail|error|invalid|already exists|required/i.test(message) ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
          {message}
        </div>
      )}

      <div className="bg-white p-4 rounded-lg shadow mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Team</label>
          <select
            value={team}
            onChange={(e) => {
              setTeam(e.target.value);
              setEditingId(null);
            }}
            className="border p-2 rounded bg-white text-gray-900"
            disabled={isAdmin() ? false : teams.length <= 1}
          >
            <option value="">Select team...</option>
            {teams.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={startCreate}
          disabled={!team}
          className="btn-theme-primary px-4 py-2 rounded disabled:opacity-50"
        >
          + New lineup
        </button>
      </div>

      {editingId != null && (
        <div className="bg-white p-4 rounded-lg shadow mb-4 border-l-4 border-theme-primary">
          <h3 className="font-semibold mb-3">{editingId === 'new' ? 'New lineup' : 'Edit lineup'}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Name</label>
              <input
                type="text"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. 1v"
                className="border p-2 rounded w-full bg-white text-gray-900"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Boat class</label>
              <select
                value={draft.boatClass}
                onChange={(e) => changeBoatClass(e.target.value)}
                className="border p-2 rounded w-full bg-white text-gray-900"
              >
                {SHELL_CLASSES.map((cls) => (
                  <option key={cls.value} value={cls.value}>
                    {cls.label} · {getSeatsForBoatClass(cls.value).seats.length} seats
                  </option>
                ))}
              </select>
            </div>
          </div>
          {renderSeatEditor(draft.seats, updateSeat)}
          <div className="flex flex-wrap gap-2 mt-4">
            <button type="button" onClick={saveDraft} className="btn-theme-primary px-4 py-2 rounded">
              {editingId === 'new' ? 'Save lineup' : 'Save changes'}
            </button>
            <button
              type="button"
              onClick={() => setEditingId(null)}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading && <p className="text-sm text-gray-500">Loading lineups…</p>}

      {!loading && team && lineups.length === 0 && editingId == null && (
        <div className="bg-gray-50 border rounded-lg p-6 text-center text-gray-600">
          No saved lineups for {team} yet. Create a 1v / 2v / 3v crew to reuse in Workout Builder.
        </div>
      )}

      {byClass.map((group) => (
        <div key={group.value} className="mb-4">
          <h3 className="text-sm font-semibold text-gray-600 mb-2">
            {group.label}
            <span className="font-normal text-gray-400"> · {getSeatsForBoatClass(group.value).seats.length} seats</span>
          </h3>
          <div className="space-y-2">
            {group.lineups.map((lineup) => {
              const names = filledNames(lineup.seats);
              return (
                <div key={lineup.id} className="bg-white rounded-lg shadow p-3 sm:p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{lineup.name}</p>
                      <p className="text-xs text-gray-500">
                        {names.length} / {(lineup.seats || []).length} seated
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(lineup)}
                        className="text-sm px-3 py-1.5 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteLineup(lineup)}
                        className="text-sm px-3 py-1.5 rounded bg-red-50 text-red-700 hover:bg-red-100"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(lineup.seats || []).map((seat) => (
                      <span
                        key={seat.seat}
                        className={`text-xs px-2 py-1 rounded border ${seatLabelClass(seat.seat)}`}
                      >
                        {seat.seat}: {seat.athlete || '—'}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
